import React, { useState, useEffect } from 'react';

const LoginSecurityPanel = ({ adminToken }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('fields');
  const [settings, setSettings] = useState({
    field_visibility: {
      email: true,
      password: true,
      full_name: true,
      phone: false,
      username: false
    },
    field_required: {
      email: true,
      password: true,
      full_name: true,
      phone: false,
      username: false
    },
    custom_fields: [],
    captcha_enabled: false,
    captcha_questions: [],
    terms_enabled: false,
    terms_link: '/terms-of-use',
    spam_prevention_enabled: true,
    max_login_attempts: 5,
    lockout_duration_minutes: 15,
    max_registration_per_ip_per_day: 3,
    ip_whitelist: [],
    banned_ips: [],
    login_form_hidden: false,
    register_form_hidden: false,
    hidden_form_message: 'Registration and login are currently unavailable. Please check back later.'
  });
  
  const [bannedIPs, setBannedIPs] = useState([]);
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [attemptsFilter, setAttemptsFilter] = useState({
    success: 'all', // all, true, false
    email: '',
    ip: ''
  });
  const [attemptsPagination, setAttemptsPagination] = useState({
    page: 1,
    perPage: 30,
    total: 0
  });
  const [newCustomField, setNewCustomField] = useState({
    name: '',
    label: '',
    type: 'text',
    required: false,
    placeholder: '',
    options: []
  });
  const [newCaptchaQuestion, setNewCaptchaQuestion] = useState({
    question: '',
    answer: '',
    category: 'logic'
  });
  const [newBanIP, setNewBanIP] = useState({
    ip_address: '',
    reason: '',
    duration_days: null
  });
  const [editingCustomField, setEditingCustomField] = useState(null);
  const [editingCaptcha, setEditingCaptcha] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [cookieSettings, setCookieSettings] = useState({
    cookie_consent_enabled: false,
    cookie_consent_message: 'We use cookies to improve your experience. By continuing to use this site, you agree to our use of cookies.',
    cookie_consent_link: '/privacy-policy'
  });
  const [savingCookie, setSavingCookie] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Toast notification helper
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  useEffect(() => {
    loadSettings();
    loadBannedIPs();
    loadCookieSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/security/settings`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error loading security settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBannedIPs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/security/banned-ips`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBannedIPs(data.banned_ips || []);
      }
    } catch (error) {
      console.error('Error loading banned IPs:', error);
    }
  };

  const loadCookieSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCookieSettings({
          cookie_consent_enabled: data.cookie_consent_enabled || false,
          cookie_consent_message: data.cookie_consent_message || 'We use cookies to improve your experience. By continuing to use this site, you agree to our use of cookies.',
          cookie_consent_link: data.cookie_consent_link || '/privacy-policy'
        });
      }
    } catch (error) {
      console.error('Error loading cookie settings:', error);
    }
  };

  const saveCookieSettings = async () => {
    setSavingCookie(true);
    try {
      const formData = new FormData();
      formData.append('cookie_consent_enabled', cookieSettings.cookie_consent_enabled);
      formData.append('cookie_consent_message', cookieSettings.cookie_consent_message);
      formData.append('cookie_consent_link', cookieSettings.cookie_consent_link);

      const response = await fetch(`${BACKEND_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });

      if (response.ok) {
        showToast('Cookie consent settings saved!', 'success');
      } else {
        showToast('Error saving cookie settings', 'error');
      }
    } catch (error) {
      console.error('Error saving cookie settings:', error);
      showToast('Error saving cookie settings', 'error');
    } finally {
      setSavingCookie(false);
    }
  };

  const loadLoginAttempts = async (page = 1) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/security/login-attempts?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        let filtered = data.login_attempts || [];
        
        // Apply filters
        if (attemptsFilter.success !== 'all') {
          const successValue = attemptsFilter.success === 'true';
          filtered = filtered.filter(a => a.success === successValue);
        }
        if (attemptsFilter.email) {
          filtered = filtered.filter(a => a.email?.toLowerCase().includes(attemptsFilter.email.toLowerCase()));
        }
        if (attemptsFilter.ip) {
          filtered = filtered.filter(a => a.ip_address?.includes(attemptsFilter.ip));
        }
        
        // Apply pagination
        const total = filtered.length;
        const start = (page - 1) * attemptsPagination.perPage;
        const paginated = filtered.slice(start, start + attemptsPagination.perPage);
        
        setLoginAttempts(paginated);
        setAttemptsPagination({
          ...attemptsPagination,
          page: page,
          total: total
        });
      }
    } catch (error) {
      console.error('Error loading login attempts:', error);
    }
  };

  const deleteAllLoginAttempts = async () => {
    if (!window.confirm('⚠️ Are you sure you want to delete ALL login attempt logs? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/security/login-attempts/flush`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        showToast('✅ All login attempts deleted successfully', 'success');
        loadLoginAttempts(1);
      } else {
        showToast('❌ Failed to delete login attempts', 'error');
      }
    } catch (error) {
      console.error('Error deleting login attempts:', error);
      showToast('❌ Error deleting login attempts', 'error');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/security/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        showToast('✅ Security settings saved successfully!', 'success');
      } else {
        showToast('❌ Error saving settings', 'error');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('❌ Error saving settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addCustomField = () => {
    if (!newCustomField.name || !newCustomField.label) {
      showToast('Please enter field name and label', 'error');
      return;
    }
    
    if (editingCustomField) {
      // Update existing field
      setSettings({
        ...settings,
        custom_fields: settings.custom_fields.map(f => 
          f.id === editingCustomField.id ? {...newCustomField, id: f.id, order: f.order} : f
        )
      });
      setEditingCustomField(null);
      showToast('Custom field updated!', 'success');
    } else {
      // Add new field
      setSettings({
        ...settings,
        custom_fields: [...settings.custom_fields, {
          ...newCustomField,
          id: Date.now().toString(),
          order: settings.custom_fields.length
        }]
      });
      showToast('Custom field added!', 'success');
    }
    
    setNewCustomField({
      name: '',
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: []
    });
  };

  const editCustomField = (field) => {
    setEditingCustomField(field);
    setNewCustomField({
      name: field.name,
      label: field.label,
      type: field.type,
      required: field.required,
      placeholder: field.placeholder || '',
      options: field.options || []
    });
  };

  const cancelEditCustomField = () => {
    setEditingCustomField(null);
    setNewCustomField({
      name: '',
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: []
    });
  };

  const removeCustomField = (fieldId) => {
    setSettings({
      ...settings,
      custom_fields: settings.custom_fields.filter(f => f.id !== fieldId)
    });
    showToast('Custom field removed', 'success');
  };

  const addCaptchaQuestion = () => {
    if (!newCaptchaQuestion.question || !newCaptchaQuestion.answer) {
      showToast('Please enter question and answer', 'error');
      return;
    }
    
    if (editingCaptcha) {
      // Update existing question
      setSettings({
        ...settings,
        captcha_questions: settings.captcha_questions.map(q => 
          q.id === editingCaptcha.id ? {...newCaptchaQuestion, id: q.id} : q
        )
      });
      setEditingCaptcha(null);
      showToast('CAPTCHA question updated!', 'success');
    } else {
      // Add new question
      setSettings({
        ...settings,
        captcha_questions: [...settings.captcha_questions, {
          ...newCaptchaQuestion,
          id: Date.now().toString()
        }]
      });
      showToast('CAPTCHA question added!', 'success');
    }
    
    setNewCaptchaQuestion({
      question: '',
      answer: '',
      category: 'logic'
    });
  };

  const editCaptchaQuestion = (question) => {
    setEditingCaptcha(question);
    setNewCaptchaQuestion({
      question: question.question,
      answer: question.answer,
      category: question.category
    });
  };

  const cancelEditCaptcha = () => {
    setEditingCaptcha(null);
    setNewCaptchaQuestion({
      question: '',
      answer: '',
      category: 'logic'
    });
  };

  const removeCaptchaQuestion = (questionId) => {
    setSettings({
      ...settings,
      captcha_questions: settings.captcha_questions.filter(q => q.id !== questionId)
    });
    showToast('CAPTCHA question removed', 'success');
  };

  const banIP = async () => {
    if (!newBanIP.ip_address) {
      showToast('Please enter IP address', 'error');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/security/ban-ip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newBanIP)
      });
      
      if (response.ok) {
        showToast('✅ IP banned successfully!', 'success');
        loadBannedIPs();
        setNewBanIP({ ip_address: '', reason: '', duration_days: null });
      } else {
        const error = await response.json();
        showToast(`❌ Error: ${error.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error banning IP:', error);
      showToast('❌ Error banning IP', 'error');
    }
  };

  const unbanIP = async (ipAddress) => {
    if (!window.confirm(`Unban IP ${ipAddress}?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/security/unban-ip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ip_address: ipAddress })
      });
      
      if (response.ok) {
        showToast('✅ IP unbanned successfully!', 'success');
        loadBannedIPs();
      } else {
        showToast('❌ Error unbanning IP', 'error');
      }
    } catch (error) {
      console.error('Error unbanning IP:', error);
      showToast('❌ Error unbanning IP', 'error');
    }
  };

  const tabs = [
    { id: 'access', label: '🔐 Form Access', icon: '🔐' },
    { id: 'fields', label: '📝 Field Controls', icon: '📝' },
    { id: 'custom', label: '➕ Custom Fields', icon: '➕' },
    { id: 'captcha', label: '🤖 CAPTCHA', icon: '🤖' },
    { id: 'terms', label: '📜 Terms & Conditions', icon: '📜' },
    { id: 'spam', label: '🛡️ Spam Prevention', icon: '🛡️' },
    { id: 'bans', label: '🚫 IP Bans', icon: '🚫' },
    { id: 'attempts', label: '📊 Login Attempts', icon: '📊' },
    { id: 'cookies', label: '🍪 Cookie Consent', icon: '🍪' }
  ];

  if (loading) {
    return <div className="p-6 text-center">Loading security settings...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg animate-fade-in ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold dark:text-white">🔒 Login Security</h2>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : '💾 Save All Settings'}
        </button>
      </div>

      {/* Vertical Tab Navigation */}
      <div className="flex gap-6">
        <div className="w-64 space-y-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'attempts') loadLoginAttempts();
              }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <span className="text-lg mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
          {/* Form Access Control Tab */}
          {activeTab === 'access' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white mb-4">Form Access Control</h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                Hide login and/or register forms from the landing page. Useful when deploying or doing maintenance.
                Admin login at /admin-panel is never affected.
              </p>

              {/* Warning when both hidden */}
              {settings.login_form_hidden && settings.register_form_hidden && (
                <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-red-700 dark:text-red-300 font-medium">
                    Both forms are hidden. No new visitors can log in or register. Only already logged-in users and admin (/admin-panel) can access the site.
                  </p>
                </div>
              )}

              {/* Login Form Toggle */}
              <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.login_form_hidden}
                  onChange={(e) => setSettings({...settings, login_form_hidden: e.target.checked})}
                  className="rounded w-5 h-5"
                />
                <div>
                  <span className="font-medium dark:text-white">Hide Login Form</span>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Visitors cannot log in. Already logged-in users are not affected.</p>
                </div>
              </label>

              {/* Register Form Toggle */}
              <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.register_form_hidden}
                  onChange={(e) => setSettings({...settings, register_form_hidden: e.target.checked})}
                  className="rounded w-5 h-5"
                />
                <div>
                  <span className="font-medium dark:text-white">Hide Register Form</span>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Visitors cannot create new accounts.</p>
                </div>
              </label>

              {/* Custom Message */}
              {(settings.login_form_hidden || settings.register_form_hidden) && (
                <div>
                  <label className="block text-sm font-medium dark:text-white mb-2">
                    Custom Message for Visitors ({(settings.hidden_form_message || '').length}/500)
                  </label>
                  <textarea
                    value={settings.hidden_form_message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setSettings({...settings, hidden_form_message: e.target.value});
                      }
                    }}
                    className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                    rows={3}
                    placeholder="Message shown to visitors when forms are hidden..."
                  />
                  {/* Preview */}
                  <div className="mt-4 p-4 bg-gray-900 rounded-lg border border-gray-700">
                    <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Preview (what visitors see)</p>
                    <div className="text-center py-4">
                      <div className="text-3xl mb-3">🔒</div>
                      <p className="text-gray-300">{settings.hidden_form_message || 'No message set'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Field Controls Tab */}
          {activeTab === 'fields' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white mb-4">Standard Field Controls</h3>
              
              {['email', 'password', 'full_name', 'phone', 'username'].map(field => (
                <div key={field} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-medium dark:text-white capitalize">
                      {field.replace('_', ' ')}
                      {['email', 'password'].includes(field) && ' (Always Required)'}
                    </span>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={settings.field_visibility[field]}
                          onChange={(e) => setSettings({
                            ...settings,
                            field_visibility: {
                              ...settings.field_visibility,
                              [field]: e.target.checked
                            }
                          })}
                          disabled={['email', 'password'].includes(field)}
                          className="rounded"
                        />
                        <span className="text-sm dark:text-gray-300">Visible</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={settings.field_required[field]}
                          onChange={(e) => setSettings({
                            ...settings,
                            field_required: {
                              ...settings.field_required,
                              [field]: e.target.checked
                            }
                          })}
                          disabled={['email', 'password'].includes(field)}
                          className="rounded"
                        />
                        <span className="text-sm dark:text-gray-300">Required</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Custom Fields Tab */}
          {activeTab === 'custom' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white mb-4">Custom Registration Fields</h3>
              
              {/* Add Custom Field Form */}
              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-3">
                <input
                  type="text"
                  placeholder="Field Name (e.g., country, bio)"
                  value={newCustomField.name}
                  onChange={(e) => setNewCustomField({...newCustomField, name: e.target.value})}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                />
                <input
                  type="text"
                  placeholder="Field Label (e.g., Country, Bio)"
                  value={newCustomField.label}
                  onChange={(e) => setNewCustomField({...newCustomField, label: e.target.value})}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                />
                <select
                  value={newCustomField.type}
                  onChange={(e) => setNewCustomField({...newCustomField, type: e.target.value})}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                >
                  <option value="text">Text</option>
                  <option value="textarea">Textarea</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="select">Select</option>
                </select>
                <input
                  type="text"
                  placeholder="Placeholder text"
                  value={newCustomField.placeholder}
                  onChange={(e) => setNewCustomField({...newCustomField, placeholder: e.target.value})}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                />
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newCustomField.required}
                    onChange={(e) => setNewCustomField({...newCustomField, required: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm dark:text-gray-300">Required Field</span>
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={addCustomField}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    {editingCustomField ? '✏️ Update Field' : '➕ Add Custom Field'}
                  </button>
                  {editingCustomField && (
                    <button
                      onClick={cancelEditCustomField}
                      className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              {/* Custom Fields List */}
              <div className="space-y-2">
                {settings.custom_fields.map(field => (
                  <div key={field.id} className="p-3 border border-gray-200 dark:border-gray-700 rounded flex justify-between items-center">
                    <div>
                      <span className="font-medium dark:text-white">{field.label}</span>
                      <span className="text-sm text-gray-500 ml-2">({field.type}{field.required ? ', required' : ''})</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => editCustomField(field)}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => removeCustomField(field.id)}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                {settings.custom_fields.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No custom fields added</p>
                )}
              </div>
            </div>
          )}

          {/* CAPTCHA Tab */}
          {activeTab === 'captcha' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white mb-4">Simple Logic CAPTCHA</h3>
              
              <label className="flex items-center gap-2 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <input
                  type="checkbox"
                  checked={settings.captcha_enabled}
                  onChange={(e) => setSettings({...settings, captcha_enabled: e.target.checked})}
                  className="rounded"
                />
                <span className="font-medium dark:text-white">Enable CAPTCHA on Registration</span>
              </label>

              {/* Add CAPTCHA Question */}
              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-3">
                <input
                  type="text"
                  placeholder="Question (e.g., What color is grass?)"
                  value={newCaptchaQuestion.question}
                  onChange={(e) => setNewCaptchaQuestion({...newCaptchaQuestion, question: e.target.value})}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                />
                <input
                  type="text"
                  placeholder="Answer (e.g., green)"
                  value={newCaptchaQuestion.answer}
                  onChange={(e) => setNewCaptchaQuestion({...newCaptchaQuestion, answer: e.target.value})}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                />
                <select
                  value={newCaptchaQuestion.category}
                  onChange={(e) => setNewCaptchaQuestion({...newCaptchaQuestion, category: e.target.value})}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                >
                  <option value="logic">Logic</option>
                  <option value="color">Color</option>
                  <option value="number">Number</option>
                  <option value="day">Day</option>
                  <option value="spelling">Spelling</option>
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={addCaptchaQuestion}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    {editingCaptcha ? '✏️ Update Question' : '➕ Add Question'}
                  </button>
                  {editingCaptcha && (
                    <button
                      onClick={cancelEditCaptcha}
                      className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              {/* Questions List */}
              <div className="space-y-2">
                {settings.captcha_questions.map(q => (
                  <div key={q.id} className="p-3 border border-gray-200 dark:border-gray-700 rounded flex justify-between items-center">
                    <div>
                      <div className="font-medium dark:text-white">{q.question}</div>
                      <div className="text-sm text-gray-500">Answer: {q.answer} • Category: {q.category}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => editCaptchaQuestion(q)}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => removeCaptchaQuestion(q.id)}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Terms & Conditions Tab */}
          {activeTab === 'terms' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white mb-4">Terms & Conditions</h3>
              
              <label className="flex items-center gap-2 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <input
                  type="checkbox"
                  checked={settings.terms_enabled}
                  onChange={(e) => setSettings({...settings, terms_enabled: e.target.checked})}
                  className="rounded"
                />
                <span className="font-medium dark:text-white">Require T&C Acceptance on Registration</span>
              </label>

              <div className="space-y-2">
                <label className="block text-sm font-medium dark:text-white">T&C Page Link</label>
                <input
                  type="text"
                  value={settings.terms_link}
                  onChange={(e) => setSettings({...settings, terms_link: e.target.value})}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                  placeholder="/terms-of-use"
                />
                <p className="text-sm text-gray-500">Link to your existing Terms & Conditions page</p>
              </div>
            </div>
          )}

          {/* Spam Prevention Tab */}
          {activeTab === 'spam' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white mb-4">Spam Prevention Settings</h3>
              
              <label className="flex items-center gap-2 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <input
                  type="checkbox"
                  checked={settings.spam_prevention_enabled}
                  onChange={(e) => setSettings({...settings, spam_prevention_enabled: e.target.checked})}
                  className="rounded"
                />
                <span className="font-medium dark:text-white">Enable Spam Prevention</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium dark:text-white mb-2">Max Login Attempts</label>
                  <input
                    type="number"
                    value={settings.max_login_attempts}
                    onChange={(e) => setSettings({...settings, max_login_attempts: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                    min="1"
                    max="20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium dark:text-white mb-2">Lockout Duration (minutes)</label>
                  <input
                    type="number"
                    value={settings.lockout_duration_minutes}
                    onChange={(e) => setSettings({...settings, lockout_duration_minutes: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                    min="5"
                    max="1440"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium dark:text-white mb-2">Max Registrations per IP/Day</label>
                  <input
                    type="number"
                    value={settings.max_registration_per_ip_per_day}
                    onChange={(e) => setSettings({...settings, max_registration_per_ip_per_day: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                    min="1"
                    max="50"
                  />
                </div>
              </div>
            </div>
          )}

          {/* IP Bans Tab */}
          {activeTab === 'bans' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white mb-4">IP Ban Management</h3>
              
              {/* Ban IP Form */}
              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-3">
                <input
                  type="text"
                  placeholder="IP Address (e.g., 192.168.1.100)"
                  value={newBanIP.ip_address}
                  onChange={(e) => setNewBanIP({...newBanIP, ip_address: e.target.value})}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                />
                <input
                  type="text"
                  placeholder="Reason for ban"
                  value={newBanIP.reason}
                  onChange={(e) => setNewBanIP({...newBanIP, reason: e.target.value})}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                />
                <input
                  type="number"
                  placeholder="Duration in days (leave empty for permanent)"
                  value={newBanIP.duration_days || ''}
                  onChange={(e) => setNewBanIP({...newBanIP, duration_days: e.target.value ? parseInt(e.target.value) : null})}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                />
                <button
                  onClick={banIP}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  🚫 Ban IP Address
                </button>
              </div>

              {/* Banned IPs List */}
              <div className="space-y-2">
                <h4 className="font-medium dark:text-white">Banned IP Addresses ({bannedIPs.length})</h4>
                {bannedIPs.map(ban => (
                  <div key={ban.id} className="p-3 border border-red-200 dark:border-red-900 rounded flex justify-between items-center">
                    <div>
                      <div className="font-medium dark:text-white">{ban.ip_address}</div>
                      <div className="text-sm text-gray-500">
                        {ban.reason} • 
                        {ban.expires_at ? ` Expires: ${new Date(ban.expires_at).toLocaleString()}` : ' Permanent'}
                      </div>
                    </div>
                    <button
                      onClick={() => unbanIP(ban.ip_address)}
                      className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Unban
                    </button>
                  </div>
                ))}
                {bannedIPs.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No banned IPs</p>
                )}
              </div>
            </div>
          )}

          {/* Login Attempts Tab */}
          {activeTab === 'attempts' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold dark:text-white">Recent Login Attempts</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setAttemptsFilter({ success: 'all', email: '', ip: '' });
                      loadLoginAttempts(1);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    🔄 Refresh
                  </button>
                  <button
                    onClick={deleteAllLoginAttempts}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    🗑️ Delete All
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div>
                  <label className="block text-sm font-medium dark:text-white mb-1">Status</label>
                  <select
                    value={attemptsFilter.success}
                    onChange={(e) => {
                      setAttemptsFilter({...attemptsFilter, success: e.target.value});
                      setTimeout(() => loadLoginAttempts(1), 100);
                    }}
                    className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                  >
                    <option value="all">All</option>
                    <option value="true">✅ Success Only</option>
                    <option value="false">❌ Failed Only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-white mb-1">Email</label>
                  <input
                    type="text"
                    placeholder="Filter by email..."
                    value={attemptsFilter.email}
                    onChange={(e) => {
                      setAttemptsFilter({...attemptsFilter, email: e.target.value});
                      setTimeout(() => loadLoginAttempts(1), 300);
                    }}
                    className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-white mb-1">IP Address</label>
                  <input
                    type="text"
                    placeholder="Filter by IP..."
                    value={attemptsFilter.ip}
                    onChange={(e) => {
                      setAttemptsFilter({...attemptsFilter, ip: e.target.value});
                      setTimeout(() => loadLoginAttempts(1), 300);
                    }}
                    className="w-full px-3 py-2 rounded border dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>

              {/* Results */}
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing {loginAttempts.length} of {attemptsPagination.total} results
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {loginAttempts.map(attempt => (
                  <div key={attempt.id} className={`p-3 border rounded ${attempt.success ? 'border-green-200 dark:border-green-900' : 'border-red-200 dark:border-red-900'}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium dark:text-white">
                          {attempt.success ? '✅' : '❌'} {attempt.email || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-500">
                          IP: {attempt.ip_address} • {new Date(attempt.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${attempt.success ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                        {attempt.success ? 'Success' : 'Failed'}
                      </span>
                    </div>
                  </div>
                ))}
                {loginAttempts.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No login attempts found</p>
                )}
              </div>

              {/* Pagination */}
              {attemptsPagination.total > attemptsPagination.perPage && (
                <div className="flex justify-center gap-2 items-center">
                  <button
                    onClick={() => loadLoginAttempts(attemptsPagination.page - 1)}
                    disabled={attemptsPagination.page === 1}
                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    ← Previous
                  </button>
                  <span className="text-sm dark:text-white">
                    Page {attemptsPagination.page} of {Math.ceil(attemptsPagination.total / attemptsPagination.perPage)}
                  </span>
                  <button
                    onClick={() => loadLoginAttempts(attemptsPagination.page + 1)}
                    disabled={attemptsPagination.page >= Math.ceil(attemptsPagination.total / attemptsPagination.perPage)}
                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Cookie Consent (GDPR) Tab */}
          {activeTab === 'cookies' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold dark:text-white">Cookie Consent (GDPR)</h3>
                <button
                  onClick={saveCookieSettings}
                  disabled={savingCookie}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingCookie ? 'Saving...' : 'Save Cookie Settings'}
                </button>
              </div>

              <p className="text-sm text-gray-500 dark:text-gray-400">
                Show a cookie consent banner at the bottom of the screen for all visitors. Required for GDPR compliance if you use cookies or analytics.
              </p>

              {/* Enable/Disable Toggle */}
              <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={cookieSettings.cookie_consent_enabled}
                  onChange={(e) => setCookieSettings({...cookieSettings, cookie_consent_enabled: e.target.checked})}
                  className="w-5 h-5 rounded text-blue-600"
                />
                <div>
                  <span className="font-medium dark:text-white">Enable Cookie Consent Banner</span>
                  <p className="text-sm text-gray-500">When enabled, visitors see a consent banner at the bottom of every page until they click "Accept"</p>
                </div>
              </label>

              {/* Status Indicator */}
              <div className={`p-3 rounded-lg text-sm font-medium ${
                cookieSettings.cookie_consent_enabled
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
              }`}>
                {cookieSettings.cookie_consent_enabled
                  ? 'Banner is ACTIVE - visitors will see the consent banner'
                  : 'Banner is OFF - no consent banner shown to visitors'
                }
              </div>

              {/* Consent Message */}
              <div className="space-y-2">
                <label className="block text-sm font-medium dark:text-white">Consent Message</label>
                <textarea
                  value={cookieSettings.cookie_consent_message}
                  onChange={(e) => setCookieSettings({...cookieSettings, cookie_consent_message: e.target.value})}
                  className="w-full px-3 py-2 rounded-lg border dark:bg-gray-800 dark:text-white dark:border-gray-700 min-h-[80px]"
                  placeholder="We use cookies to improve your experience..."
                />
                <p className="text-xs text-gray-500">This text is shown to visitors in the consent banner</p>
              </div>

              {/* Learn More Link */}
              <div className="space-y-2">
                <label className="block text-sm font-medium dark:text-white">Privacy Policy Link</label>
                <input
                  type="text"
                  value={cookieSettings.cookie_consent_link}
                  onChange={(e) => setCookieSettings({...cookieSettings, cookie_consent_link: e.target.value})}
                  className="w-full px-3 py-2 rounded-lg border dark:bg-gray-800 dark:text-white dark:border-gray-700"
                  placeholder="/privacy-policy"
                />
                <p className="text-xs text-gray-500">Link shown as "Learn more" in the banner. Points to your privacy policy page.</p>
              </div>

              {/* Preview */}
              <div className="space-y-2">
                <label className="block text-sm font-medium dark:text-white">Preview</label>
                <div className="p-4 bg-white dark:bg-gray-700 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <p className="flex-1 text-sm text-gray-600 dark:text-gray-300">
                      {cookieSettings.cookie_consent_message}{' '}
                      <span className="text-blue-600 font-medium underline">Learn more</span>
                    </p>
                    <span className="flex-shrink-0 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl">
                      Accept
                    </span>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">How it works</h4>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li>- Banner shows at bottom of screen on all pages</li>
                  <li>- When visitor clicks "Accept", preference is saved in their browser</li>
                  <li>- Banner won't show again unless they clear their browser data</li>
                  <li>- Works on both login page and inside the app</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginSecurityPanel;
